import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHome, FiTool, FiSettings, FiTrendingUp } = FiIcons;

const Services = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      icon: FiHome,
      title: 'Residential Construction',
      description: 'Custom homes, renovations, and residential projects built to your specifications.',
      features: ['Custom Home Building', 'Kitchen & Bathroom Remodeling', 'Home Additions', 'Basement Finishing'],
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=500&h=300&fit=crop'
    },
    {
      icon: FiTool,
      title: 'Commercial Construction',
      description: 'Professional commercial buildings and office spaces for businesses.',
      features: ['Office Buildings', 'Retail Spaces', 'Warehouses', 'Medical Facilities'],
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=500&h=300&fit=crop'
    },
    {
      icon: FiSettings,
      title: 'Industrial Projects',
      description: 'Large-scale industrial construction and infrastructure development.',
      features: ['Manufacturing Facilities', 'Infrastructure Projects', 'Industrial Complexes', 'Utility Buildings'],
      image: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=500&h=300&fit=crop'
    },
    {
      icon: FiTrendingUp,
      title: 'Project Management',
      description: 'Comprehensive project management services from planning to completion.',
      features: ['Project Planning', 'Budget Management', 'Timeline Coordination', 'Quality Control'],
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=500&h=300&fit=crop'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Construction Services - Residential, Commercial & Industrial | BuildCorp</title>
        <meta name="description" content="Professional construction services including residential, commercial, industrial projects and project management. Expert builders with quality guaranteed." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-navy-900 text-white py-20">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1920&h=600&fit=crop)'
          }}
        ></div>
        <div className="relative container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl font-bold mb-4">Our Services</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Comprehensive construction services for residential, commercial, and industrial projects.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="space-y-16">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                    <SafeIcon icon={service.icon} className="w-8 h-8 text-primary-500" />
                  </div>
                  <h2 className="text-3xl font-bold text-navy-900 mb-4">{service.title}</h2>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center space-x-3">
                        <div className="bg-primary-500 w-2 h-2 rounded-full"></div>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button className="bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors">
                    Learn More
                  </button>
                </div>
                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <img 
                    src={service.image}
                    alt={service.title}
                    className="w-full rounded-lg shadow-lg"
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">Our Process</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We follow a systematic approach to ensure every project is completed successfully.
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', description: 'Initial meeting to discuss your project requirements and vision.' },
              { step: '02', title: 'Planning', description: 'Detailed planning and design phase with timeline and budget.' },
              { step: '03', title: 'Construction', description: 'Professional construction with regular progress updates.' },
              { step: '04', title: 'Completion', description: 'Final inspection and handover of your completed project.' }
            ].map((process, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="text-center"
              >
                <div className="bg-primary-500 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-navy-900 mb-3">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Start Your Project?</h2>
          <p className="text-xl mb-8 text-primary-100">
            Contact us today to discuss your construction needs and get a free quote.
          </p>
          <button className="bg-white text-primary-500 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
            Get Free Consultation
          </button>
        </div>
      </section>
    </>
  );
};

export default Services;